import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { ArrowLeft, Plus, Trash2, Send, Check, AlertCircle, DollarSign } from 'lucide-react';

const ExpensesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    monto: '',
    fecha_pago: '',
    dias_anticipacion: 7,
    recurrencia: 'manual',
    notas: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    loadExpenses();
  }, [user]);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/expenses');
      setExpenses(response.data.expenses || []);
    } catch (error) {
      console.error('Error loading expenses:', error);
      setError('Error al cargar gastos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/api/admin/expenses', {
        ...formData,
        monto: parseFloat(formData.monto)
      });
      
      setSuccess('Gasto creado exitosamente');
      setShowForm(false);
      setFormData({
        nombre: '',
        monto: '',
        fecha_pago: '',
        dias_anticipacion: 7,
        recurrencia: 'manual',
        notas: ''
      });
      loadExpenses();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al crear gasto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (expenseId, nombre) => {
    if (!window.confirm(`¿Eliminar el gasto "${nombre}"?`)) return;

    try {
      await api.delete(`/api/admin/expenses/${expenseId}`);
      setSuccess('Gasto eliminado exitosamente');
      loadExpenses();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al eliminar gasto');
    }
  };

  const handleSendReminder = async (expenseId, nombre) => {
    try {
      await api.post(`/api/admin/expenses/${expenseId}/send-reminder`);
      setSuccess(`Recordatorio enviado para: ${nombre}`);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al enviar recordatorio');
    }
  };

  const handleMarkPaid = async (expenseId, nombre) => {
    if (!window.confirm(`¿Marcar como pagado "${nombre}"?`)) return;

    try {
      const response = await api.patch(`/api/admin/expenses/${expenseId}/mark-paid`);
      setSuccess(response.data.message);
      loadExpenses();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al marcar como pagado');
    }
  };

  const getExpenseStatus = (fechaPago) => {
    const now = new Date();
    const fecha = new Date(fechaPago);
    const daysUntil = Math.floor((fecha - now) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) {
      return { color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30', label: 'VENCIDO', days: daysUntil };
    } else if (daysUntil <= 7) {
      return { color: 'text-yellow-500', bg: 'bg-yellow-100 dark:bg-yellow-900/30', label: 'PRÓXIMO', days: daysUntil };
    } else {
      return { color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30', label: 'OK', days: daysUntil };
    }
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const recurrenciaOptions = [
    { value: 'manual', label: 'Manual (una vez)' },
    { value: 'mensual', label: 'Mensual' },
    { value: 'bimestral', label: 'Bimestral' },
    { value: 'trimestral', label: 'Trimestral' },
    { value: 'semestral', label: 'Semestral' },
    { value: 'anual', label: 'Anual' }
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center space-x-3">
                <DollarSign className="w-8 h-8 text-blue-500" />
                <div>
                  <h1 className="text-2xl font-bold">Gastos Administrativos</h1>
                  <p className="text-sm text-gray-500">Gestiona recordatorios de pagos</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Agregar Gasto</span>
            </button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{error}</span>
            <button onClick={() => setError('')} className="absolute top-0 right-0 px-4 py-3">
              <span className="text-2xl">&times;</span>
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{success}</span>
            <button onClick={() => setSuccess('')} className="absolute top-0 right-0 px-4 py-3">
              <span className="text-2xl">&times;</span>
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Form */}
        {showForm && (
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 mb-6`}>
            <h2 className="text-xl font-bold mb-4">Nuevo Gasto</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nombre del Gasto *</label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                    placeholder="Ej: Hosting VPS"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Monto ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.monto}
                    onChange={(e) => setFormData({ ...formData, monto: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                    placeholder="0.00"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Fecha de Pago *</label>
                  <input
                    type="date"
                    value={formData.fecha_pago}
                    onChange={(e) => setFormData({ ...formData, fecha_pago: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Recordar con (días antes)</label>
                  <input
                    type="number"
                    value={formData.dias_anticipacion}
                    onChange={(e) => setFormData({ ...formData, dias_anticipacion: parseInt(e.target.value) })}
                    className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Recurrencia</label>
                  <select
                    value={formData.recurrencia}
                    onChange={(e) => setFormData({ ...formData, recurrencia: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                  >
                    {recurrenciaOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Notas Adicionales</label>
                  <textarea
                    value={formData.notas}
                    onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
                    rows="3"
                    placeholder="Información adicional..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className={`px-4 py-2 rounded-lg ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Guardando...' : 'Guardar Gasto'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Expenses List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-4 text-gray-500">Cargando gastos...</p>
          </div>
        ) : expenses.length === 0 ? (
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-12 text-center`}>
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No hay gastos registrados</h3>
            <p className="text-gray-500 mb-6">Agrega tu primer gasto para comenzar</p>
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Agregar Primer Gasto
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {expenses.map((expense) => {
              const status = getExpenseStatus(expense.fecha_pago);
              return (
                <div
                  key={expense.id}
                  className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-bold">{expense.nombre}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.bg} ${status.color}`}>
                          {status.label} {status.days >= 0 ? `(${status.days}d)` : `(+${Math.abs(status.days)}d)`}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Monto:</span>
                          <p className="font-semibold text-lg">${expense.monto.toFixed(2)}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Fecha de Pago:</span>
                          <p className="font-semibold">{formatDate(expense.fecha_pago)}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Recordar:</span>
                          <p className="font-semibold">{expense.dias_anticipacion} días antes</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Recurrencia:</span>
                          <p className="font-semibold capitalize">{expense.recurrencia}</p>
                        </div>
                      </div>
                      {expense.notas && (
                        <div className="mt-3">
                          <span className="text-gray-500 text-sm">Notas:</span>
                          <p className="text-sm mt-1">{expense.notas}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-row md:flex-col space-x-2 md:space-x-0 md:space-y-2 mt-4 md:mt-0 md:ml-4">
                      <button
                        onClick={() => handleSendReminder(expense.id, expense.nombre)}
                        className="flex items-center justify-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                        title="Enviar recordatorio ahora"
                      >
                        <Send className="w-4 h-4" />
                        <span className="hidden md:inline">Recordar</span>
                      </button>
                      <button
                        onClick={() => handleMarkPaid(expense.id, expense.nombre)}
                        className="flex items-center justify-center space-x-1 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                        title="Marcar como pagado"
                      >
                        <Check className="w-4 h-4" />
                        <span className="hidden md:inline">Pagado</span>
                      </button>
                      <button
                        onClick={() => handleDelete(expense.id, expense.nombre)}
                        className="flex items-center justify-center space-x-1 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden md:inline">Eliminar</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpensesPage;
